import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaBook, 
  FaQuestion, 
  FaClock,
  FaGraduationCap
} from 'react-icons/fa';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import './guide.css';
import { auth, db } from '../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';

const Guide = () => {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Define guide sections with icons and descriptions
  const guideSections = [
    {
      title: 'Anki Guide',
      icon: <FaGraduationCap className="gde_mn_section-icon" />,
      description: 'Master Anki for USMLE preparation',
      path: '/community/guide/anki',
      color: '#FF6B6B' // # Pastel red
    },
    {
      title: 'USMLE Libraries',
      icon: <FaBook className="gde_mn_section-icon" />,
      description: 'Navigate through essential USMLE resources',
      path: '/community/guide/libraries',
      color: '#4ECDC4' // # Pastel teal
    },
    {
      title: 'Question-Taking',
      icon: <FaQuestion className="gde_mn_section-icon" />,
      description: 'Strategies for answering USMLE questions',
      path: '/community/guide/questions',
      color: '#95A5A6' // # Pastel gray
    },
    {
      title: 'Exam Day',
      icon: <FaClock className="gde_mn_section-icon" />,
      description: 'Prepare for your exam day',
      path: '/community/guide/exam-day',
      color: '#A8E6CF' // # Pastel green
    }
  ];

  return (
    <div className={`gde_mn_page-container ${isDarkMode ? 'gde_mn_dark-mode' : ''}`}>
      {/* # Pass theme props to header */}
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />

      <div className="gde_mn_guide-container">
        <div className="gde_mn_header">
          <h1>USMLE Study Guide</h1>
          <p>Comprehensive guides to help you succeed in your USMLE journey</p>
        </div>

        <div className="gde_mn_sections-grid">
          {guideSections.map((section, index) => (
            <div 
              key={index}
              className="gde_mn_section-card"
              onClick={() => navigate(section.path)}
              style={{ cursor: 'pointer' }}
            >
              <div 
                className="gde_mn_icon-container"
                style={{ '--icon-color': section.color }}
              >
                <div className="gde_mn_icon-inner">
                  {section.icon}
                </div>
                <div className="gde_mn_icon-ring"></div>
              </div>
              <h3>{section.title}</h3>
              <p>{section.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* # Pass theme prop to footer */}
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default Guide;
